import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'

import RozmowaService from '@/services/RozmowaService'
import SearchActionsBar from '@/widgets/SearchActionsBar'
import LoadMoreSection from '@/widgets/LoadMoreSection'
import RozmowyTable from './components/RozmowyTable'
import useRozmowyData from './hooks/useRozmowyData'
import PlacowkaFilterModal from '../../../../filter-modals/components/PlacowkaFilterModal'
import DateRangeFilterModal from '../../../../filter-modals/components/DateRangeFilterModal'
import StatusFilterModal from '../../../../filter-modals/components/StatusFilterModal'

const PAST_OPPORTUNITIES_NOTIFICATION_STORAGE_KEY = 'rozmowy_past_opportunities_notification_at'
const PAST_OPPORTUNITIES_NOTIFICATION_COOLDOWN_MS = 30 * 60 * 1000

function canShowPastOpportunitiesNotification() {
	const lastShown = localStorage.getItem(PAST_OPPORTUNITIES_NOTIFICATION_STORAGE_KEY)
	if (!lastShown) {
		return true
	}

	const lastShownTime = Number(lastShown)
	if (!Number.isFinite(lastShownTime)) {
		return true
	}

	return Date.now() - lastShownTime > PAST_OPPORTUNITIES_NOTIFICATION_COOLDOWN_MS
}

function markPastOpportunitiesNotificationShown() {
	localStorage.setItem(PAST_OPPORTUNITIES_NOTIFICATION_STORAGE_KEY, String(Date.now()))
}

export default function Rozmowy() {
	const { t } = useTranslation()
	const {
		data,
		loading,
		loadingMore,
		error,
		totalCount,
		hasMore,
		loadData,
		handleLoadMore,
		handleSearchChange,
		handleSort,
		handleApplyPlacowkaFilter,
		handleApplyDigRozmowyFilter,
		handleApplyDigNastepnejRozmowyFilter,
		handleApplySalesOpportunityFilter,
		searchQuery,
		sortColumn,
		sortDirection,
		isPlacowkaFilterOpen,
		isDigRozmowyFilterOpen,
		isDigNastepnejRozmowyFilterOpen,
		isSalesOpportunityFilterOpen,
		selectedPlacowkaId,
		digFromDate,
		digToDate,
		digNastepnejRozmowyFromDate,
		digNastepnejRozmowyToDate,
		hasDigRozmowyFilter,
		hasDigNastepnejRozmowyFilter,
		hasSalesOpportunityFilter,
		selectedSalesOpportunity,
		setIsPlacowkaFilterOpen,
		setIsDigRozmowyFilterOpen,
		setIsDigNastepnejRozmowyFilterOpen,
		setIsSalesOpportunityFilterOpen,
		clearError,
	} = useRozmowyData()

	const salesOpportunityFilterOptions = useMemo(
		() => [
			{ value: 'true', label: t('conversations.list.filters.salesOpportunityYes') },
			{ value: 'false', label: t('conversations.list.filters.salesOpportunityNo') },
		],
		[t],
	)

	useEffect(() => {
		if (error) {
			Swal.fire({
				title: t('conversations.messages.errorTitle'),
				text: error,
				icon: 'error',
			}).then(() => {
				clearError()
			})
		}
	}, [error, clearError, t])

	useEffect(() => {
		loadData()
	}, [loadData])

	useEffect(() => {
		let cancelled = false

		async function fetchOpportunitiesCount() {
			const result = await RozmowaService.getOpportunitiesCount()

			if (cancelled || result.error || result.count <= 0 || !canShowPastOpportunitiesNotification()) {
				return
			}

			markPastOpportunitiesNotificationShown()

			await Swal.fire({
				text: t('conversations.list.messages.pastOpportunitiesCount', { count: result.count }),
				icon: 'warning',
			})
		}

		fetchOpportunitiesCount()

		return () => {
			cancelled = true
		}
	}, [t])

	const buttons = [
		{
			type: 'add',
			to: '/komunikacja/rozmowy/add',
			label: t('conversations.list.actions.addNew'),
		},
	]

	return (
		<>
			<SearchActionsBar
				value={searchQuery}
				onChange={handleSearchChange}
				placeholder={t('conversations.list.searchPlaceholder')}
				buttons={buttons}
			/>

			<RozmowyTable
				data={data}
				loading={loading}
				sortColumn={sortColumn}
				sortDirection={sortDirection}
				onSort={handleSort}
				hasDigRozmowyFilter={hasDigRozmowyFilter}
				onOpenDigRozmowyFilter={() => setIsDigRozmowyFilterOpen(true)}
				hasDigNastepnejRozmowyFilter={hasDigNastepnejRozmowyFilter}
				onOpenDigNastepnejRozmowyFilter={() => setIsDigNastepnejRozmowyFilterOpen(true)}
				hasSalesOpportunityFilter={hasSalesOpportunityFilter}
				onOpenSalesOpportunityFilter={() => setIsSalesOpportunityFilterOpen(true)}
				hasPlacowkaFilter={!!selectedPlacowkaId}
				onOpenPlacowkaFilter={() => setIsPlacowkaFilterOpen(true)}
			/>

			<LoadMoreSection
				hasMore={hasMore}
				loadingMore={loadingMore}
				onLoadMore={handleLoadMore}
				dataLength={data.length}
				totalCount={totalCount}
			/>
			<PlacowkaFilterModal
				isOpen={isPlacowkaFilterOpen}
				onClose={() => setIsPlacowkaFilterOpen(false)}
				selectedPlacowkaId={selectedPlacowkaId}
				onApply={handleApplyPlacowkaFilter}
				title={t('conversations.list.filters.facilityTitle')}
				description={t('conversations.list.filters.facilityDescription')}
				applyLabel={t('conversations.list.filters.apply')}
				clearLabel={t('conversations.list.filters.clear')}
			/>
			<DateRangeFilterModal
				isOpen={isDigRozmowyFilterOpen}
				onClose={() => setIsDigRozmowyFilterOpen(false)}
				fromDate={digFromDate}
				toDate={digToDate}
				onApply={handleApplyDigRozmowyFilter}
				title={t('conversations.list.filters.conversationDateTitle')}
				description={t('conversations.list.filters.conversationDateDescription')}
				fromLabel={t('conversations.list.filters.fromDate')}
				toLabel={t('conversations.list.filters.toDate')}
				applyLabel={t('conversations.list.filters.apply')}
				clearLabel={t('conversations.list.filters.clear')}
				cancelLabel={t('conversations.list.filters.cancel')}
				invalidRangeMessage={t('conversations.list.filters.invalidRange')}
			/>
			<DateRangeFilterModal
				isOpen={isDigNastepnejRozmowyFilterOpen}
				onClose={() => setIsDigNastepnejRozmowyFilterOpen(false)}
				fromDate={digNastepnejRozmowyFromDate}
				toDate={digNastepnejRozmowyToDate}
				onApply={handleApplyDigNastepnejRozmowyFilter}
				title={t('conversations.list.filters.nextConversationDateTitle')}
				description={t('conversations.list.filters.nextConversationDateDescription')}
				fromLabel={t('conversations.list.filters.fromDate')}
				toLabel={t('conversations.list.filters.toDate')}
				applyLabel={t('conversations.list.filters.apply')}
				clearLabel={t('conversations.list.filters.clear')}
				cancelLabel={t('conversations.list.filters.cancel')}
				invalidRangeMessage={t('conversations.list.filters.invalidRange')}
			/>
			<StatusFilterModal
				isOpen={isSalesOpportunityFilterOpen}
				onClose={() => setIsSalesOpportunityFilterOpen(false)}
				selectedStatus={selectedSalesOpportunity}
				onApply={handleApplySalesOpportunityFilter}
				statusOptions={salesOpportunityFilterOptions}
				title={t('conversations.list.filters.salesOpportunityTitle')}
				description={t('conversations.list.filters.salesOpportunityDescription')}
				applyLabel={t('conversations.list.filters.apply')}
				clearLabel={t('conversations.list.filters.clear')}
			/>
		</>
	)
}
